import classnames from 'classnames2';
import React, { useEffect, useRef } from 'react';

import { Header } from '../Header';
import { Icon } from '../Icon/Icon';
import { LoadingSpinner } from '../LoadingSpinner';
import { ITextProps, Text } from '../Text';

import styles from './Toast.module.scss';

import { ToastIllustration } from './ToastIllustration';

export enum ToastVariant {
  info = 'info',
  success = 'success',
  failure = 'failure',
  warning = 'warning'
}

export type IToastVariant = keyof typeof ToastVariant;

export interface IToastProps {
  /** Title for the Toast. */
  title: string;
  /** Body of the description Text for the Toast. */
  body: string;
  /** Changes the visual display of the Toast. */
  variant?: IToastVariant;
  /** Boolean to determine if a loading spinner should show. */
  showLoadingSpinner?: boolean;
  /** Value for `data-test` attribute, used for testing purposes only */
  dataTest?: string;
  /** Expose custom classname to clients */
  classname?: string;
  /** Allow clients to determine if they want the Toast component absolutely positioned or not */
  toastAbsolutePosition?: boolean;
  /** Props that are used on the Text component for the description section of the Toast */
  descriptionTextProps?: ITextProps;
  /** Callback function that is called when Toast is closed */
  onClose?(): void;
}

export type IToastComponent = React.FC<IToastProps>;

export const Toast: React.FC<IToastProps> = ({
  variant = ToastVariant.info,
  body,
  title,
  dataTest,
  onClose,
  showLoadingSpinner,
  descriptionTextProps,
  classname,
  toastAbsolutePosition = true
}) => {
  const toastRef = useRef(null);
  const handleKeyDown = (e) => toastRef?.current?.contains(e.target) && e.key === 'Escape' && onClose();

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={classnames({
        [styles.container]: true,
        [styles.toastAbsolutePosition]: toastAbsolutePosition,
        [styles.variantFailure]: variant === 'failure',
        [styles.variantInfo]: variant === 'info',
        [styles.variantWarning]: variant === 'warning',
        [styles.variantSuccess]: variant === 'success',
        [classname]: classname
      })}
      data-test={dataTest}
      role="alert"
      ref={toastRef}
    >
      <div className={styles.toastCloseBar}>
        {onClose && (
          <button data-test="onCloseButton" className={styles.closeButton} aria-label="Close" onClick={onClose}>
            <Icon icon="action-close-small" className={styles.closeIcon} />
          </button>
        )}
      </div>
      <div className={styles.contentContainer}>
        <div>
          {showLoadingSpinner ? (
            <LoadingSpinner variant="inline" dataTest="toastLoadingSpinner" />
          ) : (
            <ToastIllustration className={styles.illustration} variant={variant} />
          )}
        </div>
        <div>
          <Header variant="h3" as="span" className={styles.header}>
            {title}
          </Header>
          <Text {...descriptionTextProps}>{body}</Text>
        </div>
      </div>
    </div>
  );
};
